export type PurchasePatientType = 'new' | 'existing';

export interface PurchaseItemInput {
  itemId: string | number;
  itemName: string;
  itemCategory?: string;
  price: number;
  quantity?: number;
  locationId?: string | number;
  locationName?: string;
  clinicianId?: string | number;
  clinicianName?: string;
}

export interface PurchaseEventInput {
  transactionId: string | number;
  value: number;
  currency: string;
  items: PurchaseItemInput[];
  patientType?: PurchasePatientType;
  /**
   * Server-derived new-vs-existing patient status (no prior non-temporary appointments or
   * non-temp class attendances as of this booking)
   */
  isNewPatient?: boolean;
}

function finiteMoney(value: number | undefined | null): number {
  return Number.isFinite(value) ? Math.max(value as number, 0) : 0;
}

function hasValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

export function createPurchaseItem(input: PurchaseItemInput): Record<string, unknown> {
  return {
    item_id: input.itemId?.toString(),
    item_name: input.itemName,
    ...(input.itemCategory ? { item_category: input.itemCategory } : {}),
    price: finiteMoney(input.price),
    quantity: input.quantity ?? 1,
    ...(hasValue(input.locationId) ? { location_id: input.locationId?.toString() } : {}),
    ...(input.locationName ? { location_name: input.locationName } : {}),
    ...(hasValue(input.clinicianId) ? { clinician_id: input.clinicianId?.toString() } : {}),
    ...(input.clinicianName ? { clinician_name: input.clinicianName } : {})
  };
}

export function createPurchaseEvent(input: PurchaseEventInput): Record<string, unknown> {
  return {
    ecommerce: {
      transaction_id: input.transactionId.toString(),
      value: finiteMoney(input.value),
      currency: input.currency,
      items: input.items.map(createPurchaseItem)
    },
    ...(input.patientType ? { patient_type: input.patientType } : {}),
    ...(typeof input.isNewPatient === 'boolean' ? { is_new_patient: input.isNewPatient } : {})
  };
}

export function flattenPurchaseEventForGtag(
  params: Record<string, unknown>
): Record<string, unknown> {
  const { ecommerce, ...rest } = params;
  if (!ecommerce || typeof ecommerce !== 'object') return params;
  return { ...rest, ...(ecommerce as Record<string, unknown>) };
}

/** Returns the first Connect category containing the service. */
export function findServiceCategoryTitle(
  serviceId: string | undefined,
  categories: Array<{ orderedServices?: Array<string>; title?: string }> = []
): string | undefined {
  if (!serviceId) return undefined;
  return categories.find((category) => category.orderedServices?.includes(serviceId))?.title;
}

export function getPurchasePatientType(isAuthenticated: boolean): PurchasePatientType {
  return isAuthenticated ? 'existing' : 'new';
}
